import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Trophy, Users, Flame, Clock, Copy, Crown, Plus, LogIn,
  Sparkles, X, Medal,
} from "lucide-react";
import {
  familyLeaderboard, listLeagues, createLeague, joinLeague,
  leagueLeaderboard, leaveLeague,
} from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const RANK_BG = {
  1: "bg-[#FFE066] dark:bg-[#F6E05E]",
  2: "bg-[#D4C4FB] dark:bg-[#B794F4]",
  3: "bg-[#FFB38E] dark:bg-[#F6AD55]",
};
const AVATAR_COLORS = ["#D4C4FB", "#FFE066", "#9DFFB0", "#FFB38E", "#90E0FF"];

function Avatar({ seed, size = 48 }) {
  const url = `https://api.dicebear.com/7.x/adventurer/svg?seed=${encodeURIComponent(seed || "anon")}`;
  const color = AVATAR_COLORS[(seed?.charCodeAt(0) || 0) % AVATAR_COLORS.length];
  return (
    <div
      className="nb-border rounded-xl overflow-hidden flex-shrink-0"
      style={{ background: color, width: size, height: size }}
    >
      <img src={url} alt={seed} className="w-full h-full" />
    </div>
  );
}

function RankBadge({ rank }) {
  return (
    <div
      className={`w-12 h-12 rounded-xl nb-border flex items-center justify-center font-display font-black text-xl flex-shrink-0 ${
        RANK_BG[rank] || "bg-white dark:bg-zinc-900"
      } text-black`}
      data-testid={`rank-badge-${rank}`}
    >
      {rank <= 3 ? <Medal className="w-5 h-5" strokeWidth={2.5} /> : `#${rank}`}
    </div>
  );
}

function Row({ row, mine, showParent }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: row.rank * 0.04 }}
      className={`nb-card p-4 flex items-center gap-4 ${mine ? "ring-4 ring-offset-2 ring-offset-[#FAFAFA] dark:ring-offset-zinc-950 ring-[#D4C4FB] dark:ring-[#B794F4]" : ""}`}
      data-testid={`leaderboard-row-${row.child_id}`}
    >
      <RankBadge rank={row.rank} />
      <Avatar seed={row.avatar_seed} />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <div className="font-display text-lg font-extrabold truncate" data-testid={`row-name-${row.child_id}`}>
            {row.name}
          </div>
          {mine && (
            <span className="text-[10px] font-extrabold uppercase tracking-wider bg-[#D4C4FB] dark:bg-[#B794F4] text-black px-1.5 py-0.5 rounded">
              You
            </span>
          )}
        </div>
        <div className="text-xs font-bold text-zinc-500 flex gap-2 flex-wrap">
          <span>Year {row.year_group}</span>
          <span>·</span>
          <span>{row.exam_board?.replace(" GCSE", "")}</span>
          {showParent && row.parent_name && (
            <>
              <span>·</span>
              <span>{row.parent_name}&apos;s</span>
            </>
          )}
        </div>
      </div>
      <div className="text-right flex flex-col items-end gap-1">
        <div className="flex items-center gap-2 font-display text-xl font-black">
          <Sparkles className="w-4 h-4 text-[#B794F4]" strokeWidth={3} />
          <span data-testid={`row-score-${row.child_id}`}>{row.score}</span>
        </div>
        <div className="flex gap-2 text-[10px] font-extrabold uppercase tracking-wider text-zinc-500">
          <span className="flex items-center gap-0.5"><Flame className="w-3 h-3" />{row.streak}</span>
          <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" />{row.week_minutes}m</span>
        </div>
      </div>
    </motion.div>
  );
}

function Empty({ icon: Icon, title, body, cta, onCta, ctaTestId }) {
  return (
    <div className="nb-card p-10 text-center">
      <Icon className="w-12 h-12 mx-auto mb-3" strokeWidth={2} />
      <div className="font-display text-2xl font-extrabold mb-2">{title}</div>
      <p className="text-zinc-600 dark:text-zinc-400 font-medium mb-5 max-w-sm mx-auto text-sm">{body}</p>
      {cta && (
        <button data-testid={ctaTestId} onClick={onCta} className="nb-button px-5 py-3 bg-[#D4C4FB] dark:bg-[#B794F4]">
          {cta}
        </button>
      )}
    </div>
  );
}

// ============================================================================

function FamilyTab({ activeChildId }) {
  const [data, setData] = useState(null);
  useEffect(() => { familyLeaderboard().then(setData); }, []);
  if (!data) return <div className="font-bold text-zinc-500">Loading…</div>;
  if (!data.rows.length) {
    return <Empty icon={Users} title="No siblings yet" body="Add a second child to start competing. (Family plan supports up to 3 kids.)" />;
  }
  if (data.rows.length === 1) {
    return (
      <div>
        <Row row={data.rows[0]} mine={data.rows[0].child_id === activeChildId} />
        <div className="mt-6 nb-border rounded-xl p-4 bg-[#FFE066] dark:bg-[#F6E05E] text-black text-sm font-bold">
          Add a sibling to unlock the family leaderboard, or join a friends league below.
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="family-leaderboard-rows">
      {data.rows.map((r) => (
        <Row key={r.child_id} row={r} mine={r.child_id === activeChildId} />
      ))}
    </div>
  );
}

// ============================================================================

function CreateJoinDialog({ kind, onClose, onDone }) {
  const [val, setVal] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!val.trim()) return;
    setSaving(true);
    try {
      const r = kind === "create"
        ? await createLeague(val.trim())
        : await joinLeague(val.trim().toUpperCase());
      onDone(r);
      onClose();
      toast.success(kind === "create" ? `League "${r.name}" created` : `Joined "${r.name}"`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="nb-card p-6 w-full max-w-md nb-shadow-xl bg-white dark:bg-zinc-900">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl font-extrabold">
            {kind === "create" ? "New friends league" : "Join a league"}
          </h2>
          <button type="button" onClick={onClose} className="nb-button p-2"><X className="w-4 h-4" /></button>
        </div>
        {kind === "create" ? (
          <>
            <label className="label-caps block mb-2">League name</label>
            <input
              data-testid="league-name-input"
              autoFocus required maxLength={50} value={val}
              onChange={(e) => setVal(e.target.value)}
              placeholder="e.g. Year 10 squad"
              className="nb-input mb-5"
            />
            <p className="text-xs font-medium text-zinc-500 mb-4">
              You&apos;ll get a 6-letter code to share with other parents. All children of joined parents compete.
            </p>
            <button type="submit" disabled={saving || !val.trim()} data-testid="league-create-submit" className="nb-button w-full py-3 bg-[#9DFFB0] dark:bg-[#68D391]">
              {saving ? "Creating…" : "Create league"}
            </button>
          </>
        ) : (
          <>
            <label className="label-caps block mb-2">Invite code (6 letters)</label>
            <input
              data-testid="league-code-input"
              autoFocus required maxLength={6} minLength={6} value={val}
              onChange={(e) => setVal(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
              placeholder="e.g. 2PNGDG"
              className="nb-input mb-5 text-center text-2xl tracking-[0.4em] font-display font-black"
            />
            <button type="submit" disabled={saving || val.length !== 6} data-testid="league-join-submit" className="nb-button w-full py-3 bg-[#D4C4FB] dark:bg-[#B794F4]">
              {saving ? "Joining…" : "Join league"}
            </button>
          </>
        )}
      </form>
    </div>
  );
}

function LeagueTab({ activeChildId }) {
  const [leagues, setLeagues] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [board, setBoard] = useState(null);
  const [dialog, setDialog] = useState(null); // "create" | "join" | null

  const load = async () => {
    const list = await listLeagues();
    setLeagues(list);
    if (list.length && !selectedId) setSelectedId(list[0].id);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!selectedId) { setBoard(null); return; }
    setBoard(null);
    leagueLeaderboard(selectedId).then(setBoard).catch((e) => {
      toast.error(e?.response?.data?.detail || "Failed");
    });
  }, [selectedId]);

  const copy = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Code ${code} copied`);
  };

  const leave = async (id) => {
    if (!window.confirm("Leave this league?")) return;
    await leaveLeague(id);
    setSelectedId(null);
    await load();
  };

  if (leagues === null) return <div className="font-bold text-zinc-500">Loading…</div>;

  return (
    <div>
      <div className="flex gap-2 mb-5 flex-wrap">
        <button data-testid="league-create-btn" onClick={() => setDialog("create")} className="nb-button px-4 py-2 bg-[#9DFFB0] dark:bg-[#68D391] flex items-center gap-1.5 text-sm">
          <Plus className="w-4 h-4" strokeWidth={3} /> New league
        </button>
        <button data-testid="league-join-btn" onClick={() => setDialog("join")} className="nb-button px-4 py-2 flex items-center gap-1.5 text-sm">
          <LogIn className="w-4 h-4" /> Join with code
        </button>
      </div>

      {leagues.length === 0 ? (
        <Empty
          icon={Users}
          title="No friends leagues yet"
          body="Create a league and share the code with friends from school. Their kids will appear here ranked side-by-side."
          cta="Create my first league"
          onCta={() => setDialog("create")}
          ctaTestId="leagues-empty-create"
        />
      ) : (
        <>
          {/* League chips */}
          <div className="flex gap-2 mb-5 overflow-x-auto pb-2" data-testid="league-tabs">
            {leagues.map((l) => (
              <button
                key={l.id}
                data-testid={`league-tab-${l.id}`}
                onClick={() => setSelectedId(l.id)}
                className={`nb-border rounded-xl px-3 py-2 text-sm font-bold flex-shrink-0 ${
                  selectedId === l.id ? "bg-[#D4C4FB] dark:bg-[#B794F4] nb-shadow text-black" : "bg-white dark:bg-zinc-900"
                }`}
              >
                {l.name}
                <span className="ml-2 text-xs opacity-70">{l.member_count} {l.member_count === 1 ? "fam" : "fams"}</span>
              </button>
            ))}
          </div>

          {board && (
            <>
              {/* Code banner */}
              <div className="nb-card p-4 mb-5 bg-[#FFE066] dark:bg-[#F6E05E] text-black flex items-center justify-between gap-3 flex-wrap" data-testid="league-code-banner">
                <div>
                  <div className="label-caps">Invite code</div>
                  <div className="font-display font-black text-3xl tracking-[0.3em]" data-testid="league-code">
                    {board.league.code}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => copy(board.league.code)} data-testid="league-copy" className="nb-button px-3 py-2 bg-black text-white dark:bg-white dark:text-black flex items-center gap-1.5">
                    <Copy className="w-4 h-4" /> Copy
                  </button>
                  <button onClick={() => leave(board.league.id)} data-testid="league-leave" className="nb-button px-3 py-2">
                    Leave
                  </button>
                </div>
              </div>

              {/* Leaderboard */}
              {board.rows.length === 0 ? (
                <Empty icon={Users} title="No kids yet" body="Share the code so other parents can join, their children will start showing up here." />
              ) : (
                <div className="space-y-3" data-testid="league-leaderboard-rows">
                  {board.rows.map((r) => (
                    <Row key={r.child_id} row={r} mine={r.child_id === activeChildId} showParent />
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}

      <AnimatePresence>
        {dialog && (
          <CreateJoinDialog
            kind={dialog}
            onClose={() => setDialog(null)}
            onDone={async (l) => {
              await load();
              setSelectedId(l.id);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================================================

const TABS = [
  { key: "family", label: "Family", icon: Users },
  { key: "leagues", label: "Friends", icon: Crown },
];

export default function Leaderboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("family");

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-zinc-950" data-testid="leaderboard-page">
      <header className="nb-border border-l-0 border-r-0 border-t-0 bg-white dark:bg-zinc-900 sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-6 py-3 flex items-center justify-between gap-3">
          <button onClick={() => navigate(-1)} data-testid="leaderboard-back" className="nb-border rounded-xl px-3 py-2 font-bold text-sm flex items-center gap-2 hover:nb-shadow">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="font-display font-extrabold text-xl flex items-center gap-2">
            <Trophy className="w-5 h-5" /> Leaderboard
          </div>
          <div className="w-16" />
        </div>
      </header>

      <div className="max-w-3xl mx-auto p-6 sm:p-10">
        <div className="mb-6">
          <div className="label-caps mb-2">Rank by XP + this week&apos;s study time</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
            Who&apos;s{" "}
            <span className="bg-[#9DFFB0] dark:bg-[#68D391] px-3 nb-border rounded-2xl inline-block text-black">
              top of class?
            </span>
          </h1>
        </div>

        <div className="flex gap-2 mb-6 nb-border rounded-xl p-1 bg-white dark:bg-zinc-900 w-fit">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`leaderboard-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-lg font-bold text-sm flex items-center gap-1.5 transition-all ${
                tab === t.key ? "bg-[#D4C4FB] dark:bg-[#B794F4] text-black nb-shadow" : "hover:bg-zinc-50 dark:hover:bg-zinc-800"
              }`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>

        {tab === "family" ? (
          <FamilyTab activeChildId={user?.active_child_id} />
        ) : (
          <LeagueTab activeChildId={user?.active_child_id} />
        )}
      </div>
    </div>
  );
}
